import React, { useEffect, useState } from "react";
import PageHelp from "@/components/PageHelp";
import OfflineReadyBadge, { useOfflineReadyProjects } from "@/components/OfflineReadyBadge";
import { useNavigate } from "react-router-dom";
import { Plus, FolderKanban, MapPin, Trash2, Pencil } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/lib/perms";
import { PROJECT_STATUS, PROJECT_STATUS_STYLE } from "@/lib/constants";
import ProjectFormDialog from "@/components/ProjectFormDialog";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

function ProjectBar({ label, done, total, testid, color = "" }) {
  const pct = total ? Math.round(done / total * 100) : 0;
  return (
    <div data-testid={testid}>
      <div className="flex items-center justify-between text-xs text-slate-500 mb-1">
        <span>{label}</span><span className="font-mono-tech" data-testid={`${testid}-value`}>{pct}% <span className="text-slate-400">({done}/{total})</span></span>
      </div>
      <Progress value={pct} className={`h-1.5 ${color}`} />
    </div>
  );
}

export default function Projects() {
  const [projects, setProjects] = useState(null);
  const offlineReady = useOfflineReadyProjects();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = can(user, "plans:manage");

  const load = () => api.get("/projects").then((r) => setProjects(r.data));
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    await api.delete(`/projects/${id}`);
    toast.success("Projekt gelöscht");
    load();
  };

  return (
    <div className="space-y-6" data-testid="projects-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-3xl sm:text-4xl font-head font-extrabold tracking-tight text-slate-900">Projekte</h1>
            <PageHelp id="projekte" title="Projekte" text="Verwalten Sie Ihre Einrichtungen und Prüfobjekte. Als Administrator legen Sie mit „Neues Projekt“ eine neue Einrichtung an; öffnen Sie eine Karte für Gebäude, Geschosse und Baupläne." />
          </div>
          <p className="text-slate-500 mt-1">Verwalten Sie Ihre Einrichtungen und Prüfobjekte und Dokumentationen.</p>
        </div>
        {isAdmin && <Button className="h-11" data-testid="new-project-btn" onClick={() => setOpen(true)}><Plus className="h-4 w-4 mr-2" />Neues Projekt</Button>}
        {isAdmin && <ProjectFormDialog open={open} onOpenChange={setOpen} project={null} onSaved={(d) => d?.id && navigate(`/projects/${d.id}`)} />}
        {isAdmin && <ProjectFormDialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)} project={editing} onSaved={load} />}
      </div>

      {!projects ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">{Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-40 rounded-xl" />)}</div>
      ) : projects.length === 0 ? (
        <Card className="p-12 text-center">
          <FolderKanban className="h-12 w-12 mx-auto text-slate-300" />
          <p className="mt-4 text-slate-500">Noch keine Projekte. Legen Sie Ihr erstes Bauprojekt an.</p>
        </Card>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {projects.map((p) => {
            const st = PROJECT_STATUS.find((s) => s.value === p.status);
            return (
              <Card key={p.id} className="p-5 hover:shadow-md transition-shadow fade-up group relative" data-testid={`project-card-${p.id}`}>
                <div onClick={() => navigate(`/projects/${p.id}`)} className="cursor-pointer">
                  <div className={`flex items-start justify-between gap-2 ${isAdmin ? "pr-16" : ""}`}>
                    <div className="min-w-0">
                      {(p.field_visibility?.number ?? true) && <div className="text-xs font-mono-tech text-slate-400">{p.number || "—"}</div>}
                      <h3 className="font-head font-bold text-lg text-slate-900 truncate">{p.name}</h3>
                    </div>
                    <Badge variant="outline" className={PROJECT_STATUS_STYLE[p.status]}>{st?.label}</Badge>
                  </div>
                  <div className="mt-3 flex items-center gap-1.5 text-sm text-slate-500"><MapPin className="h-3.5 w-3.5" /><span className="truncate">{p.address || "Keine Adresse"}</span></div>
                  <div className="mt-4 space-y-2.5">
                    <ProjectBar label="Tickets abgearbeitet" done={p.tickets_done || 0} total={p.tickets_total || 0} testid={`project-progress-tickets-${p.id}`} />
                    <ProjectBar label="Mängel abgearbeitet" done={(p.total_defects || 0) - (p.open_defects || 0)} total={p.total_defects || 0} testid={`project-progress-defects-${p.id}`} color="[&>div]:bg-green-600" />
                  </div>
                  <div className="mt-2 flex items-center justify-between gap-2 flex-wrap">
                    <span className="text-xs text-slate-500" data-testid={`project-open-defects-${p.id}`}>{p.open_tickets || 0} offene Tickets · {p.open_defects || 0} offene Mängel</span>
                    <OfflineReadyBadge projectId={p.id} ready={offlineReady} />
                  </div>
                </div>
                <div className="absolute top-3 right-3 flex items-center gap-0.5">
                {isAdmin && (
                  <button type="button" onClick={(e) => { e.stopPropagation(); setEditing(p); }} title="Projekt bearbeiten"
                    className="p-1.5 rounded-md bg-white/90 hover:bg-orange-50 text-slate-400 hover:text-primary transition-colors" data-testid={`edit-project-${p.id}`}>
                    <Pencil className="h-4 w-4" />
                  </button>
                )}
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    {isAdmin && <button className="opacity-0 group-hover:opacity-100 transition-opacity p-1.5 rounded-md hover:bg-red-50 text-slate-300 hover:text-red-500" data-testid={`delete-project-${p.id}`}><Trash2 className="h-4 w-4" /></button>}
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader><AlertDialogTitle>Projekt löschen?</AlertDialogTitle><AlertDialogDescription>Alle Gebäude, Baupläne, Positionen und Tickets werden unwiderruflich gelöscht.</AlertDialogDescription></AlertDialogHeader>
                    <AlertDialogFooter><AlertDialogCancel>Abbrechen</AlertDialogCancel><AlertDialogAction onClick={() => remove(p.id)} className="bg-red-600 hover:bg-red-700">Löschen</AlertDialogAction></AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
